import { useNavigate } from 'react-router-dom';
import type { ArticlePost } from '@/lib/articles';

interface Props {
  post: ArticlePost;
  variant?: 'default' | 'compact';
}

const BlogCard = ({ post, variant = 'default' }: Props) => {
  const navigate = useNavigate();
  const handleClick = () => navigate(`/blog/${post.id}`);

  if (variant === 'compact') {
    return (
      <div
        onClick={handleClick}
        className="group cursor-pointer flex gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors"
      >
        <div className="w-16 h-14 flex-shrink-0 rounded-lg overflow-hidden">
          <img src={post.coverImage} alt={post.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
        </div>
        <div className="flex-1 min-w-0">
          <span className={`text-[10px] font-bold bg-gradient-to-r ${post.gradient} bg-clip-text text-transparent`}>{post.category}</span>
          <h4 className="text-xs font-semibold text-[#0A1628] leading-snug mt-0.5 line-clamp-2 group-hover:text-violet-700 transition-colors">{post.title}</h4>
          <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-400">
            <span>{post.readTime}分鐘</span>
            <span>·</span>
            <span className="flex items-center gap-0.5"><i className="ri-eye-line" />{(post.views / 1000).toFixed(1)}K</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={handleClick}
      className="group cursor-pointer bg-white rounded-2xl overflow-hidden border border-slate-100 hover:border-slate-200 hover:-translate-y-1 transition-all duration-300 flex flex-col"
    >
      {/* Cover */}
      <div className="h-44 relative overflow-hidden flex-shrink-0">
        <img
          src={post.coverImage}
          alt={post.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
        <span className={`absolute top-3 left-3 bg-gradient-to-r ${post.gradient} text-white text-[10px] font-bold px-2.5 py-1 rounded-full`}>
          {post.category}
        </span>
        <span className="absolute bottom-3 right-3 bg-black/40 text-white text-[10px] px-2 py-0.5 rounded-full backdrop-blur-sm flex items-center gap-1">
          <i className="ri-time-line" />{post.readTime} 分鐘
        </span>
      </div>

      <div className="p-5 flex flex-col flex-1">
        {/* Title & excerpt */}
        <h3 className="text-sm font-bold text-[#0A1628] leading-snug mb-2 line-clamp-2 group-hover:text-[#1a2a48] transition-colors">
          {post.title}
        </h3>
        <p className="text-xs text-slate-500 leading-relaxed line-clamp-2 mb-3">{post.excerpt}</p>

        {/* Tags */}
        <div className="flex flex-wrap gap-1 mb-4">
          {post.tags.slice(0, 3).map((t) => (
            <span key={t} className="px-2 py-0.5 bg-slate-50 text-slate-400 text-[10px] rounded-md border border-slate-100">{t}</span>
          ))}
        </div>

        {/* Author row */}
        <div className="mt-auto pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-slate-100">
              <img src={post.authorAvatar} alt={post.author} className="w-full h-full object-cover object-top" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-bold text-[#0A1628] leading-none truncate">{post.author}</p>
              <p className="text-[10px] text-slate-400 mt-0.5 truncate">{post.authorRole} · {post.authorCompany}</p>
            </div>
          </div>
          <div className="flex items-center gap-2.5 text-[11px] text-slate-400 flex-shrink-0">
            <span className="flex items-center gap-0.5"><i className="ri-eye-line" />{(post.views / 1000).toFixed(1)}K</span>
            <span className="flex items-center gap-0.5"><i className="ri-heart-3-line" />{post.likes}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogCard;
